import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import {
  Package,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  MapPin,
  Plus,
  Eye,
  RefreshCw,
  Download,
  Calendar,
  User,
  ArrowRight,
  Building,
  TrendingDown
} from 'lucide-react'
import { inventoryService } from '@/services/inventoryService'
import { useAuth } from '@/contexts/AuthContext'
import { selectApiData } from '@/services/api'
import Button from '@/components/ui/Button'
import { formatCurrency } from '@/utils/formatters'

const InventoryDashboard = () => {
  // Permission checks removed
  const [timeRange, setTimeRange] = useState('30')

  // Fetch dashboard data
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery(
    ['inventory-stats', timeRange],
    () => inventoryService.getDashboardStats({ timeRange }),
    {
      select: (response) => {
        // console.log('Inventory stats API response:', response)
        return selectApiData(response)
      },
      onError: (error) => {
        console.log('Inventory stats API error, using mock data:', error.message)
      },
      retry: false,
      staleTime: 5 * 60 * 1000
    }
  )

  // Fetch low stock items
  const { data: lowStockData, error: lowStockError } = useQuery(
    'inventory-low-stock',
    () => inventoryService.getItems({ low_stock: true, limit: 5 }),
    {
      select: (response) => {
        // console.log('Low stock API response:', response)
        return selectApiData(response)?.items || []
      },
      onError: (error) => {
        console.log('Low stock API error:', error.message)
      },
      retry: false,
      staleTime: 5 * 60 * 1000
    }
  )

  // Fetch recent movements
  const { data: movementsData, error: movementsError } = useQuery(
    'inventory-recent-movements',
    () => inventoryService.getMovements({ limit: 5 }),
    {
      select: (response) => {
        // console.log('Movements API response:', response)
        return selectApiData(response)?.movements || []
      },
      onError: (error) => {
        console.log('Movements API error:', error.message)
      },
      retry: false,
      staleTime: 5 * 60 * 1000
    }
  )

  // Fetch locations
  const { data: locationsData, error: locationsError } = useQuery(
    'inventory-locations',
    () => inventoryService.getLocations(),
    {
      select: (response) => {
        // console.log('Locations API response:', response)
        return selectApiData(response)?.locations || []
      },
      onError: (error) => {
        console.log('Locations API error:', error.message)
      },
      retry: false,
      staleTime: 5 * 60 * 1000
    }
  )

  // Mock data for demonstration
  const mockStats = {
    totalItems: 1247,
    lowStockItems: 23,
    totalValue: 2400000,
    totalCategories: 45,
    totalLocations: 8,
    movementsToday: 15,
    topCategories: [
      { name: 'Electronics', count: 245, value: 850000 },
      { name: 'Furniture', count: 189, value: 420000 },
      { name: 'Stationery', count: 567, value: 125000 },
      { name: 'Equipment', count: 98, value: 680000 }
    ],
    stockTrend: {
      thisMonth: 1247,
      lastMonth: 1198,
      change: 4.1
    }
  }

  const mockLowStockItems = [
    {
      id: 2,
      name: 'Office Chair Ergonomic',
      sku: 'OFF-002',
      current_stock: 3,
      minimum_stock: 10,
      unit_of_measure: 'pcs',
      unit_cost: 85000,
      InventoryLocation: { name: 'Office Storage', code: 'OF-001' }
    },
    {
      id: 4,
      name: 'Printer Toner Cartridge',
      sku: 'PRT-004',
      current_stock: 2,
      minimum_stock: 8,
      unit_of_measure: 'pcs',
      unit_cost: 15000,
      InventoryLocation: { name: 'Office Storage', code: 'OF-001' }
    },
    {
      id: 5,
      name: 'Whiteboard Markers',
      sku: 'STA-005',
      current_stock: 5,
      minimum_stock: 20,
      unit_of_measure: 'pack',
      unit_cost: 2500,
      InventoryLocation: { name: 'Stationery Store', code: 'ST-001' }
    }
  ]

  const mockRecentMovements = [
    {
      id: 1,
      movement_type: 'in',
      quantity: 10,
      movement_date: '2024-01-20T09:30:00Z',
      reason: 'Purchase order received',
      InventoryItem: { name: 'Dell Laptop XPS 13', sku: 'LAP-001', unit_of_measure: 'pcs' },
      MovedBy: { first_name: 'John', last_name: 'Doe' },
      ToLocation: { name: 'Main Warehouse', code: 'WH-001' }
    },
    {
      id: 2,
      movement_type: 'out',
      quantity: -3,
      movement_date: '2024-01-18T14:15:00Z',
      reason: 'Sales order fulfillment',
      InventoryItem: { name: 'Dell Laptop XPS 13', sku: 'LAP-001', unit_of_measure: 'pcs' },
      MovedBy: { first_name: 'Jane', last_name: 'Smith' },
      FromLocation: { name: 'Main Warehouse', code: 'WH-001' }
    },
    {
      id: 3,
      movement_type: 'transfer',
      quantity: 5,
      movement_date: '2024-01-17T11:20:00Z',
      reason: 'Stock redistribution',
      InventoryItem: { name: 'Office Chair Ergonomic', sku: 'OFF-002', unit_of_measure: 'pcs' },
      MovedBy: { first_name: 'Mike', last_name: 'Johnson' },
      FromLocation: { name: 'Main Warehouse', code: 'WH-001' },
      ToLocation: { name: 'Office Storage', code: 'OF-001' }
    }
  ]

  const mockLocations = [
    { id: 1, name: 'Main Warehouse', code: 'WH-001', type: 'warehouse' },
    { id: 2, name: 'Office Storage', code: 'OF-001', type: 'office' },
    { id: 3, name: 'Stationery Store', code: 'ST-001', type: 'store' }
  ]

  // Transform real API data to match expected format
  const stats = statsData ? {
    totalItems: statsData.stats?.totalItems || 0,
    lowStockItems: statsData.stats?.lowStockCount || 0,
    totalValue: parseFloat(statsData.stats?.totalValue || 0),
    totalCategories: statsData.categoryStats?.length || 0,
    totalLocations: statsData.stats?.totalLocations || 0,
    movementsToday: statsData.recentMovements?.length || 0,
    topCategories: statsData.categoryStats?.map(cat => ({
      name: cat.category || 'Uncategorized',
      count: cat.count || 0,
      value: parseFloat(cat.totalValue || 0)
    })) || [],
    stockTrend: {
      thisMonth: statsData.stats?.totalItems || 0,
      lastMonth: Math.max(0, (statsData.stats?.totalItems || 0) - 10), // Mock calculation
      change: 4.1 // Mock value
    }
  } : (statsError ? mockStats : null)
  
  const lowStockItems = lowStockData || (lowStockError ? mockLowStockItems : [])
  const recentMovements = movementsData || (movementsError ? mockRecentMovements : [])
  const locations = locationsData || (locationsError ? mockLocations : [])
  
  // Debug current data
  console.log('InventoryDashboard - Current data:', {
    statsData,
    stats,
    lowStockItemsCount: lowStockItems?.length || 0,
    movementsCount: recentMovements?.length || 0,
    locationsCount: locations?.length || 0
  })

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in':
        return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'out':
        return <TrendingDown className="h-4 w-4 text-red-600" />
      case 'transfer':
        return <Package className="h-4 w-4 text-purple-600" />
      case 'adjustment':
        return <RefreshCw className="h-4 w-4 text-blue-600" />
      default:
        return <Package className="h-4 w-4 text-gray-600" />
    }
  }

  const getMovementColor = (type) => {
    switch (type) {
      case 'in':
        return 'text-green-600 bg-green-100'
      case 'out':
        return 'text-red-600 bg-red-100'
      case 'transfer':
        return 'text-purple-600 bg-purple-100'
      case 'adjustment':
        return 'text-blue-600 bg-blue-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Dashboard</h1>
          <p className="text-gray-600">Overview of your inventory management system</p>
          {(statsError || lowStockError || movementsError) && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 mt-2">
              Demo Mode - API Unavailable
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Link to="/inventory/items/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Package className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Items</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.totalItems?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">+{stats?.stockTrend?.change || 0}%</span>
              <span className="text-gray-500 ml-1">from last month</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.lowStockItems || 0}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Link to="/inventory/items?low_stock=true" className="text-sm text-red-600 hover:text-red-700">
              View low stock items →
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(stats?.totalValue || 0)}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              Across {stats?.totalLocations || 0} locations
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.totalCategories || 0}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              {stats?.movementsToday || 0} movements today
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Low Stock Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Low Stock Items</h3>
                <Link to="/inventory/items?low_stock=true">
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-2" />
                    View All
                  </Button>
                </Link>
              </div>
            </div>
            <div className="p-6">
              {lowStockItems.length > 0 ? (
                <div className="space-y-4">
                  {lowStockItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <div>
                          <Link 
                            to={`/inventory/items/${item.id}`}
                            className="font-medium text-gray-900 hover:text-primary-600"
                          >
                            {item.name}
                          </Link>
                          <p className="text-sm text-gray-500">{item.sku}</p>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <MapPin className="h-4 w-4" />
                            <span>{item.InventoryLocation?.name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-600">
                          {item.current_stock} {item.unit_of_measure}
                        </p>
                        <p className="text-xs text-gray-500">
                          Min: {item.minimum_stock} {item.unit_of_measure}
                        </p>
                        <p className="text-xs text-gray-500">
                          Value: {formatCurrency(item.current_stock * item.unit_cost)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No low stock items</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions & Locations */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link to="/inventory/items" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Package className="h-4 w-4 mr-2" />
                  Manage Items
                </Button>
              </Link>
              <Link to="/inventory/movements" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Stock Movements
                </Button>
              </Link>
              <Link to="/inventory/locations" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <MapPin className="h-4 w-4 mr-2" />
                  Locations
                </Button>
              </Link>
              <Link to="/inventory/reports" className="block">
                <Button variant="outline" className="w-full justify-start">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Reports
                </Button>
              </Link>
            </div>
          </div>

          {/* Locations Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Locations</h3>
              <Link to="/inventory/locations">
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View All
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {locations.slice(0, 3).map((location) => (
                <div key={location.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{location.name}</p>
                      <p className="text-xs text-gray-500">{location.code}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 capitalize">{location.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Movements */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Recent Stock Movements</h3>
            <Link to="/inventory/movements">
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                View All
              </Button>
            </Link>
          </div>
        </div>
        <div className="p-6">
          {recentMovements.length > 0 ? (
            <div className="space-y-4">
              {recentMovements.map((movement) => (
                <div key={movement.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getMovementIcon(movement.movement_type)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMovementColor(movement.movement_type)}`}>
                          {movement.movement_type.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {movement.quantity > 0 ? '+' : ''}{movement.quantity} {movement.InventoryItem?.unit_of_measure}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{movement.InventoryItem?.name}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(movement.movement_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {movement.MovedBy?.User?.first_name} {movement.MovedBy?.User?.last_name}
                        </div>
                        {(movement.FromLocation || movement.ToLocation) && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            {movement.FromLocation && <span>{movement.FromLocation.name}</span>}
                            {movement.FromLocation && movement.ToLocation && <ArrowRight className="h-3 w-3" />}
                            {movement.ToLocation && <span>{movement.ToLocation.name}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{movement.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No recent movements</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Categories */}
      {stats?.topCategories && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Categories by Value</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.topCategories.map((category, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                <h4 className="font-medium text-gray-900">{category.name}</h4>
                <p className="text-sm text-gray-500">{category.count} items</p>
                <p className="text-lg font-semibold text-primary-600">
                  {formatCurrency(category.value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default InventoryDashboard